import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import { useT } from "@/hooks/useT";

/**
 * Premium analytics showcase — large chart on dark surface with soft glow.
 */
export default function AnalyticsShowcase() {
  const t = useT();
  const bars = [42, 55, 38, 64, 71, 58, 73, 82, 76, 88, 92, 84, 79, 95, 88, 92, 96, 89, 93, 97, 99, 95, 98, 100, 96, 94, 92, 95, 97, 98];
  return (
    <section className="relative overflow-hidden surface-dark-2 text-white grain-dark">
      <div className="orb orb-orange -top-32 left-1/4 h-[480px] w-[480px] opacity-70 breathe" aria-hidden />
      <div className="orb orb-blue   bottom-0 right-0 h-[360px] w-[360px] opacity-50" aria-hidden />
      <div className="absolute inset-0 bg-dot-dark opacity-40" aria-hidden />

      <div className="container-akm relative section">
        <div className="grid items-end gap-6 md:grid-cols-12">
          <div className="md:col-span-7">
            <div className="eyebrow-dark">{t("Raporte dhe Statistika", "Reports & Analytics")}</div>
            <h2 className="mt-4 font-display text-[clamp(2rem,1.4rem+2.4vw,3rem)] font-extrabold tracking-[-0.03em] leading-[1.06] text-white">
              {t("Raporte të qarta për hotelin tuaj.", "Clear reports for your hotel.")}<br />
              <span className="text-white/70">{t("Pa numra që nuk i kupton askush.", "No numbers no one understands.")}</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="lead-dark text-[15.5px]">
              {t(
                "Okupanca, ADR, RevPAR dhe performanca e secilit kanal — të paraqitura thjesht. Eksportoni në PDF ose Excel kur ju duhet për kontabilitetin.",
                "Occupancy, ADR, RevPAR and per-channel performance — presented plainly. Export to PDF or Excel whenever accounting asks for it."
              )}
            </p>
          </div>
        </div>

        <div className="relative mt-14">
          <div className="absolute -inset-x-8 -inset-y-8 -z-10 rounded-[32px] bg-[radial-gradient(closest-side,rgba(255,122,0,0.18),transparent_70%)] blur-3xl" aria-hidden />
          <div className="float-slow overflow-hidden rounded-2xl border border-white/10 bg-[#0E1626]/85 shadow-cinematic-dark backdrop-blur-sm">
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-white/45">{t("Të ardhurat — Nëntor 2026", "Revenue — Nov 2026")}</div>
                <div className="mt-1 font-display text-[36px] font-extrabold tracking-tight text-white">
                  €38,420 <span className="inline-flex items-center gap-1 align-middle text-[13px] font-semibold text-emerald-300"><ArrowUpRight size={14} /> +18.4%</span>
                </div>
              </div>
              <div className="hidden gap-6 text-right md:flex">
                {[["ADR", "€72.40"],["RevPAR", "€60.81"],["Okupanca", "84%"]].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[11px] text-white/45">{k}</div>
                    <div className="font-display text-[16px] font-extrabold text-white">{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Chart */}
            <div className="relative px-6 py-8">
              <div className="absolute inset-x-6 top-8 grid h-44 grid-rows-4">
                {[0,1,2,3].map((i) => <div key={i} className="border-t border-white/5" />)}
              </div>
              <div className="relative flex h-44 items-end gap-1.5">
                {bars.map((v, i) => (
                  <div key={i} className="flex-1 rounded-t bg-white/5">
                    <div className="rounded-t bg-gradient-to-t from-[#FF7A00] to-[#FFB068]" style={{ height: `${v}%` }} />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-between text-[10.5px] text-white/35">
                <span>{t("1 Nën", "1 Nov")}</span>
                <span>{t("15 Nën", "15 Nov")}</span>
                <span>{t("30 Nën", "30 Nov")}</span>
              </div>
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-2 gap-px border-t border-white/10 bg-white/10 text-[12px] sm:grid-cols-4">
              {[
                [t("Direkt","Direct"),      "32%", "#FF7A00", "€12,294"],
                ["Booking.com", "41%", "#7AB7FF", "€15,752"],
                ["Airbnb",      "17%", "#FCA5A5", "€6,531"],
                [t("Të tjerë","Others"),    "10%", "#94A3B8", "€3,843"],
              ].map(([k, v, c, eur]) => (
                <div key={k} className="bg-[#0E1626] px-5 py-4">
                  <div className="flex items-center gap-2 text-[11.5px] text-white/55">
                    <span className="h-2 w-2 rounded-full" style={{ background: c }} /> {k}
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-display text-[18px] font-extrabold text-white">{v}</span>
                    <span className="text-[11px] text-white/45">{eur}</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-white/5">
                    <div className="h-full rounded-full" style={{ background: c, width: v }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Floating insight card */}
          <div className="pointer-events-none absolute -bottom-6 right-4 hidden w-[280px] md:block">
            <div className="float-slow rounded-xl border border-white/10 bg-[#0E1626] p-4 ring-soft">
              <div className="flex items-center gap-2">
                <span className="grid h-7 w-7 place-items-center rounded-md bg-[#FF7A00]/20 text-[#FFB068]">
                  <Sparkles size={14} />
                </span>
                <span className="font-display text-[13px] font-bold text-white">{t("Sugjerim për çmimin", "Pricing suggestion")}</span>
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-white/65">
                {t(
                  <>Okupanca pritet të jetë 92% nesër. Provo të rrisësh çmimin e dhomës Standard me <strong className="text-[#FFB068]">+€8</strong>.</>,
                  <>Occupancy is expected to hit 92% tomorrow. Try lifting the Standard room rate by <strong className="text-[#FFB068]">+€8</strong>.</>
                )}
              </p>
              <button className="mt-2.5 inline-flex items-center gap-1 rounded-md bg-[#FF7A00] px-2.5 py-1 text-[11px] font-semibold text-white">
                {t("Aplikoje", "Apply")} <ArrowRight size={11} />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link to="/revenue-management" className="btn-primary">{t("Çmimet dhe Të Ardhurat", "Pricing & Revenue")} <ArrowRight size={14} /></Link>
          <Link to="/platforma" className="btn-on-dark">{t("Të gjitha modulet", "All modules")}</Link>
        </div>
      </div>
    </section>
  );
}
